/**
 * Canvas Studio store (Zustand) — UI/editor state only.
 *
 * Fabric is the source of truth for geometry; this store holds the *intent*
 * (active tool, zoom readout, grid/snap, background, dirty flag, selection
 * summary for the right panel, clipboard meta, context-menu position) plus the
 * history pointer used to enable/disable the undo/redo buttons. All real Fabric
 * mutation lives in CanvasCore — never mutate Fabric from here.
 */
import { create } from 'zustand';
import { ZOOM_MIN, ZOOM_MAX, MAX_HISTORY } from './canvasConstants';

const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

export const useCanvasStore = create((set, get) => ({
  // ── Tools ──────────────────────────────────────────────────────────────────
  activeTool: 'select',
  previousTool: 'select',
  setActiveTool: (tool) =>
    set((s) => (tool === s.activeTool ? s : { previousTool: s.activeTool, activeTool: tool })),

  // ── Viewport (zoom readout; Fabric owns the transform) ───────────────────────
  zoom: 1,
  setZoom: (z) => set({ zoom: clampZoom(z) }),

  // ── Selection summary (drives the right panel) ───────────────────────────────
  // { count, kind, style:{...} } — refreshed by CanvasCore on select/modify.
  selection: { count: 0, kind: null, style: {} },
  setSelection: (selection) => set({ selection }),

  // ── History pointer (CanvasCore stores the JSON; we track positions) ─────────
  canUndo: false,
  canRedo: false,
  setHistoryFlags: (canUndo, canRedo) => set({ canUndo, canRedo }),

  // ── Grid & snap ──────────────────────────────────────────────────────────────
  gridSize: 20,
  setGridSize: (gridSize) => set({ gridSize }),
  snapEnabled: true,
  toggleSnap: () => set((s) => ({ snapEnabled: !s.snapEnabled })),

  // ── Panels ───────────────────────────────────────────────────────────────────
  // Open by default on desktop; closed on phones so the canvas isn't covered.
  rightPanelOpen: typeof window !== 'undefined' ? window.innerWidth > 760 : true,
  toggleRightPanel: () => set((s) => ({ rightPanelOpen: !s.rightPanelOpen })),
  minimapOpen: typeof window !== 'undefined' ? window.innerWidth > 760 : false,
  toggleMinimap: () => set((s) => ({ minimapOpen: !s.minimapOpen })),

  // ── Canvas paper background ────────────────────────────────────────────────────
  background: 'dots',
  setBackground: (background) => set({ background }),

  // ── Drawing brush ──────────────────────────────────────────────────────────────
  brushColor: '#0f172a',
  brushWidth: 3,
  setBrush: (patch) => set((s) => ({ ...s, ...patch })),

  // ── Sticky colour (next note) ──────────────────────────────────────────────────
  stickyColorIndex: 0,
  setStickyColorIndex: (stickyColorIndex) => set({ stickyColorIndex }),

  // ── Touch / stylus (palm rejection) ──────────────────────────────────────────────
  // Pen-priority: while a stylus is on the surface, finger/palm touches never ink
  // (handled in CanvasCore). `fingerDrawing` is the user-facing switch — it starts
  // ON, and auto-flips OFF the first time a stylus is detected so a resting palm
  // can't draw. `stylusSeen` guards that one-time auto-disable.
  fingerDrawing: true,
  setFingerDrawing: (fingerDrawing) => set({ fingerDrawing }),
  stylusSeen: false,
  setStylusSeen: (stylusSeen) => set({ stylusSeen }),

  // ── Dirty / object count ────────────────────────────────────────────────────────
  dirty: false,
  setDirty: (dirty) => set({ dirty }),
  objectCount: 0,
  setObjectCount: (objectCount) => set({ objectCount }),

  // ── Context menu ────────────────────────────────────────────────────────────────
  contextMenu: null, // { x, y } | null
  setContextMenu: (contextMenu) => set({ contextMenu }),

  // ── Clipboard flag (the real payload lives in CanvasCore) ────────────────────────
  hasClipboard: false,
  setHasClipboard: (hasClipboard) => set({ hasClipboard }),

  // Reset everything to defaults when leaving / switching canvases.
  resetEditor: () =>
    set({
      activeTool: 'select', previousTool: 'select', zoom: 1,
      selection: { count: 0, kind: null, style: {} },
      canUndo: false, canRedo: false, dirty: false, objectCount: 0,
      contextMenu: null, hasClipboard: false,
      fingerDrawing: true, stylusSeen: false,
    }),
}));

export { MAX_HISTORY };
